import { layers, namedFlavor } from '@protomaps/basemaps';
import { CONFIG } from './config.js';
import { CAPSULA } from './capsula.js';

/**
 * O MapLibre 5 exige URL absoluta para sprite e glifos. Resolver contra a
 * página mantém tudo servido pelo próprio nginx — o caminho continua
 * relativo no config, e absoluto só na hora de entregar ao MapLibre.
 */
const absoluto = (caminho) => new URL(caminho, document.baseURI).href;

/**
 * Estilo do mapa-base, servido inteiramente do próprio VPS.
 * Nenhum `glyphs` ou `sprite` apontando para domínio de terceiro.
 */
export function estiloBase() {
  return {
    version: 8,
    // O {fontstack}/{range} precisa sobreviver ao new URL, por isso a
    // substituição acontece depois de resolver a base.
    glyphs: absoluto('assets/fontes/') + '{fontstack}/{range}.pbf',
    sprite: absoluto(CONFIG.sprites),
    sources: {
      base: {
        type: 'vector',
        url: `pmtiles://${CONFIG.basemap}`,
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      },
    },
    layers: layers('base', namedFlavor('light'), { lang: 'pt' }),
  };
}

// `has` deixa passar imovel com precoRotulo nulo, que virava capsula vazia.
// Comparar como texto cobre o ausente e o nulo.
const TEM_PRECO = ['!=', ['to-string', ['get', 'precoRotulo']], ''];

const corPorTipo = [
  'match', ['get', 'tipo'],
  'apartamento', CONFIG.cor.apartamento,
  'casa', CONFIG.cor.casa,
  'loteamento', CONFIG.cor.loteamento,
  'praia', CONFIG.cor.praia,
  'empresarial', CONFIG.cor.empresarial,
  CONFIG.cor.outro,
];

/**
 * Três faixas de zoom que não se sobrepõem:
 *   até 14  -> só grupos
 *   15      -> pinos individuais
 *   16+     -> cápsulas de preço
 *
 * O limiar de agrupamento precisa ser 14 e não menor: Meireles e Aldeota
 * concentram dezenas de imóveis e ainda se sobrepõem em zoom 13.
 */
export function camadasImoveis() {
  const Z_PIN = CONFIG.zoomMaxAgrupamento + 1;   // 15
  const Z_CAP = CONFIG.zoomCapsulas;             // 16

  return [
    {
      id: 'grupos',
      type: 'circle',
      source: 'imoveis',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': CONFIG.cor.marcaTexto,
        'circle-opacity': 0.92,
        'circle-radius': ['step', ['get', 'point_count'], 16, 10, 22, 50, 30],
        'circle-stroke-width': 3,
        'circle-stroke-color': '#fff',
      },
    },
    {
      id: 'grupos-contagem',
      type: 'symbol',
      source: 'imoveis',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['Noto Sans Medium'],
        'text-size': 13,
        'text-allow-overlap': true,
      },
      paint: { 'text-color': '#fff' },
    },
    {
      // Precisão de bairro: círculo translúcido, nunca um pino, para não
      // sugerir exatidão que o dado não tem. São 40 imóveis hoje.
      id: 'aproximados',
      type: 'circle',
      source: 'imoveis',
      filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'precisao'], 'bairro']],
      minzoom: Z_PIN,
      paint: {
        'circle-color': corPorTipo,
        'circle-opacity': 0.22,
        'circle-radius': ['case', ['boolean', ['feature-state', 'ativo'], false], 18, 14],
        'circle-stroke-width': 2,
        'circle-stroke-color': corPorTipo,
        'circle-stroke-opacity': 0.65,
      },
    },
    {
      id: 'pinos',
      type: 'circle',
      source: 'imoveis',
      filter: ['all', ['!', ['has', 'point_count']], ['!=', ['get', 'precisao'], 'bairro']],
      minzoom: Z_PIN,
      maxzoom: Z_CAP,
      paint: {
        'circle-color': corPorTipo,
        'circle-radius': ['case', ['boolean', ['feature-state', 'ativo'], false], 13, 9],
        'circle-stroke-width': 3,
        'circle-stroke-color': '#fff',
      },
    },
    {
      id: 'capsulas',
      type: 'symbol',
      source: 'imoveis',
      filter: ['all', ['!', ['has', 'point_count']], TEM_PRECO],
      minzoom: Z_CAP,
      layout: {
        // Pilula branca de capsula.js; o miolo estica conforme o texto
        'icon-image': CAPSULA,
        'icon-text-fit': 'both',
        'icon-text-fit-padding': [2, 9, 2, 9],
        'icon-allow-overlap': false,
        'text-field': ['get', 'precoRotulo'],
        'text-font': ['Noto Sans Medium'],
        'text-size': 12,
        'text-padding': 6,
        // Cápsula que não couber cede lugar em vez de sobrepor a vizinha
        'text-allow-overlap': false,
        'text-ignore-placement': false,
      },
      paint: {
        // Escuro sobre o branco; ativo assume o rosa da marca
        'text-color': ['case', ['boolean', ['feature-state', 'ativo'], false],
          CONFIG.cor.marca, '#0f172a'],
      },
    },
    {
      // Imóvel sem preço não tem cápsula; continua como pino no zoom alto
      id: 'pinos-sem-preco',
      type: 'circle',
      source: 'imoveis',
      filter: [
        'all',
        ['!', ['has', 'point_count']],
        ['!', TEM_PRECO],
        ['!=', ['get', 'precisao'], 'bairro'],
      ],
      minzoom: Z_CAP,
      paint: {
        'circle-color': corPorTipo,
        'circle-radius': ['case', ['boolean', ['feature-state', 'ativo'], false], 13, 9],
        'circle-stroke-width': 3,
        'circle-stroke-color': '#fff',
      },
    },
  ];
}

export const CAMADAS_INTERATIVAS = [
  'capsulas', 'pinos', 'pinos-sem-preco', 'aproximados',
];
